import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Package, Search, RefreshCcw, Building2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RawMaterialsManagement = () => {
  const [materials, setMaterials] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  
  // Form state - simplified structure
  const [formName, setFormName] = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [formUnit, setFormUnit] = useState('');
  const [formPrice, setFormPrice] = useState(0);
  const [formDescription, setFormDescription] = useState('');
  const [formActive, setFormActive] = useState(true);
  
  // Franchise assignments with custom prices
  const [franchiseAssignments, setFranchiseAssignments] = useState({});

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  const fetchMaterials = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/raw-materials`, getAuthHeaders());
      setMaterials(response.data.materials || response.data || []);
    } catch (error) {
      console.error('Error fetching raw materials:', error);
      toast.error('Failed to load raw materials');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFranchises = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/franchises`, getAuthHeaders());
      setFranchises(response.data.franchises || []);
    } catch (error) {
      console.error('Error fetching franchises:', error);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/raw-materials/categories`, getAuthHeaders());
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  useEffect(() => {
    fetchMaterials();
    fetchFranchises();
    fetchCategories();
  }, [fetchMaterials, fetchFranchises, fetchCategories]);

  const resetForm = () => {
    setFormName('');
    setFormCategory('');
    setFormUnit('');
    setFormPrice(0);
    setFormDescription('');
    setFormActive(true);
    setFranchiseAssignments({});
    setNewCategoryName('');
    setShowNewCategoryInput(false);
  };

  const handleFranchiseToggle = (franchiseId, franchiseName, city) => {
    setFranchiseAssignments(prev => {
      const current = prev[franchiseId] || { checked: false, price: formPrice, customPrice: false };
      return {
        ...prev,
        [franchiseId]: {
          ...current,
          checked: !current.checked,
          price: current.price || formPrice,
          franchise_name: franchiseName,
          city: city
        }
      };
    });
  };

  const handleFranchisePriceChange = (franchiseId, price) => {
    const numPrice = parseFloat(price) || 0;
    setFranchiseAssignments(prev => ({
      ...prev,
      [franchiseId]: {
        ...prev[franchiseId],
        price: numPrice,
        customPrice: true
      }
    }));
  };

  const selectAllFranchises = () => {
    const allAssignments = {};
    franchises.forEach(f => {
      allAssignments[f.id] = {
        checked: true,
        price: formPrice,
        customPrice: false,
        franchise_name: f.name,
        city: f.location || f.city
      };
    });
    setFranchiseAssignments(allAssignments);
  };

  const deselectAllFranchises = () => {
    setFranchiseAssignments({});
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    
    try {
      await axios.post(`${API}/raw-materials/categories`, { name: newCategoryName.trim() }, getAuthHeaders());
      toast.success('Category added!');
      setFormCategory(newCategoryName.trim());
      setCategories(prev => [...prev, newCategoryName.trim()]);
      setNewCategoryName('');
      setShowNewCategoryInput(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add category');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formName.trim()) {
      toast.error('Please enter a material name');
      return;
    }
    if (!formCategory) {
      toast.error('Please select a category');
      return;
    }
    if (!formUnit.trim()) {
      toast.error('Please enter a unit');
      return;
    }
    if (formPrice <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    // Build assigned franchises array
    const assignedFranchises = Object.entries(franchiseAssignments)
      .filter(([_, data]) => data.checked)
      .map(([franchiseId, data]) => ({
        franchise_id: franchiseId,
        franchise_name: data.franchise_name,
        city: data.city,
        price: data.price
      }));

    const payload = {
      name: formName.trim(),
      category: formCategory,
      unit: formUnit.trim(),
      price: formPrice,
      description: formDescription.trim(),
      assigned_franchises: assignedFranchises,
      active: formActive
    };

    try {
      if (editingMaterial) {
        await axios.put(`${API}/admin/raw-materials/${editingMaterial.material_id}`, payload, getAuthHeaders());
        toast.success('Raw material updated successfully');
        setIsEditModalOpen(false);
      } else {
        await axios.post(`${API}/admin/raw-materials`, payload, getAuthHeaders());
        toast.success('Raw material added successfully');
        setIsAddModalOpen(false);
      }
      resetForm();
      fetchMaterials();
      fetchCategories();
    } catch (error) {
      console.error('Error saving raw material:', error);
      toast.error(error.response?.data?.detail || 'Failed to save raw material');
    }
  };

  const handleEdit = (material) => {
    setEditingMaterial(material);
    setFormName(material.name || '');
    setFormCategory(material.category || '');
    setFormUnit(material.unit || '');
    setFormPrice(material.price || 0);
    setFormDescription(material.description || '');
    setFormActive(material.active !== false);
    
    // Rebuild franchise assignments from material data
    const assignments = {};
    if (material.assigned_franchises && Array.isArray(material.assigned_franchises)) {
      material.assigned_franchises.forEach(af => {
        assignments[af.franchise_id] = {
          checked: true,
          price: af.price,
          customPrice: af.price !== material.price,
          franchise_name: af.franchise_name,
          city: af.city
        };
      });
    }
    setFranchiseAssignments(assignments);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (materialId) => {
    if (!window.confirm('Are you sure you want to delete this raw material?')) return;
    
    try {
      await axios.delete(`${API}/admin/raw-materials/${materialId}`, getAuthHeaders());
      toast.success('Raw material deleted');
      fetchMaterials();
    } catch (error) {
      console.error('Error deleting raw material:', error);
      toast.error('Failed to delete raw material');
    }
  };

  const filteredMaterials = materials.filter(m => 
    m.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group by category
  const groupedMaterials = filteredMaterials.reduce((acc, material) => {
    const category = material.category || 'Uncategorized';
    if (!acc[category]) acc[category] = [];
    acc[category].push(material);
    return acc;
  }, {});

  // Form JSX - reusable between Add and Edit modals
  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Name */}
      <div>
        <Label className="text-sm font-semibold">Name <span className="text-red-500">*</span></Label>
        <Input
          value={formName}
          onChange={(e) => setFormName(e.target.value)}
          placeholder="e.g., Coffee Powder Premium"
          className="mt-1"
        />
      </div>

      {/* Category */}
      <div>
        <Label className="text-sm font-semibold">Category <span className="text-red-500">*</span></Label>
        {!showNewCategoryInput ? (
          <div className="flex gap-2 mt-1">
            <Select
              value={formCategory}
              onValueChange={(value) => setFormCategory(value)}
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setShowNewCategoryInput(true)}
              title="Add new category"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex gap-2 mt-1">
            <Input
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="New category name"
              className="flex-1"
            />
            <Button
              type="button"
              size="sm"
              onClick={handleAddCategory}
              className="bg-green-600 hover:bg-green-700"
            >
              Add
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => { setShowNewCategoryInput(false); setNewCategoryName(''); }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Unit and Price in same row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-semibold">Unit <span className="text-red-500">*</span></Label>
          <Input
            value={formUnit}
            onChange={(e) => setFormUnit(e.target.value)}
            placeholder="e.g., kg, pack, piece"
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-sm font-semibold">Default Price (₹) <span className="text-red-500">*</span></Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={formPrice || ''}
            onChange={(e) => setFormPrice(parseFloat(e.target.value) || 0)}
            placeholder="0.00"
            className="mt-1"
          />
        </div>
      </div>

      {/* Description */}
      <div>
        <Label className="text-sm font-semibold">Description</Label>
        <Textarea
          value={formDescription}
          onChange={(e) => setFormDescription(e.target.value)}
          placeholder="Additional details about this raw material (shown to franchises when ordering)"
          className="mt-1"
          rows={2}
        />
      </div>

      {/* Active Toggle */}
      <div className="flex items-center gap-2">
        <Switch
          checked={formActive}
          onCheckedChange={(checked) => setFormActive(checked)}
        />
        <Label className="text-sm">Active (visible to franchises)</Label>
      </div>

      {/* Franchise Assignment Section */}
      <div className="border-t pt-4">
        <div className="flex justify-between items-center mb-3">
          <Label className="text-sm font-semibold flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Assign to Franchises
          </Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={selectAllFranchises}
              className="text-xs"
            >
              Select All
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={deselectAllFranchises}
              className="text-xs"
            >
              Deselect All
            </Button>
          </div>
        </div>
        
        <div className="max-h-60 overflow-y-auto border rounded-lg">
          <table className="w-full">
            <thead className="bg-muted sticky top-0">
              <tr>
                <th className="text-left p-2 text-xs font-medium w-8"></th>
                <th className="text-left p-2 text-xs font-medium">Franchise</th>
                <th className="text-left p-2 text-xs font-medium">City</th>
                <th className="text-right p-2 text-xs font-medium w-28">Price (₹)</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {franchises.map((franchise) => {
                const assignment = franchiseAssignments[franchise.id] || { checked: false, price: formPrice };
                return (
                  <tr 
                    key={franchise.id} 
                    className={`hover:bg-muted/50 ${assignment.checked ? 'bg-emerald-50' : ''}`}
                  >
                    <td className="p-2">
                      <Checkbox
                        checked={assignment.checked}
                        onCheckedChange={() => handleFranchiseToggle(franchise.id, franchise.name, franchise.location || franchise.city)}
                      />
                    </td>
                    <td className="p-2 text-sm font-medium">{franchise.name}</td>
                    <td className="p-2 text-sm text-muted-foreground">{franchise.location || franchise.city}</td>
                    <td className="p-2">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={assignment.checked ? (assignment.price || '') : ''}
                        onChange={(e) => handleFranchisePriceChange(franchise.id, e.target.value)}
                        disabled={!assignment.checked}
                        className={`h-8 text-right text-sm ${!assignment.checked ? 'bg-muted opacity-50' : ''}`}
                        placeholder={formPrice.toString()}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        <p className="text-xs text-muted-foreground mt-2">
          {Object.values(franchiseAssignments).filter(a => a.checked).length} of {franchises.length} franchises selected
        </p>
      </div>

      <DialogFooter className="pt-4">
        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
          {editingMaterial ? 'Update Material' : 'Add Material'}
        </Button>
      </DialogFooter>
    </form>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold text-primary">Raw Materials</h2>
          <p className="text-secondary mt-1">Manage raw materials for franchise ordering</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => { fetchMaterials(); fetchCategories(); }}
            className="rounded-full"
          >
            <RefreshCcw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={isAddModalOpen} onOpenChange={(open) => {
            setIsAddModalOpen(open);
            if (open) {
              resetForm();
              setEditingMaterial(null);
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700 rounded-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Material
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-primary">Add New Raw Material</DialogTitle>
              </DialogHeader>
              {formContent}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search materials..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Materials List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-secondary">Loading materials...</p>
        </div>
      ) : Object.keys(groupedMaterials).length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No raw materials found</p>
            <p className="text-sm text-muted-foreground mt-1">Click "Add Material" to create your first raw material</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedMaterials).map(([category, items]) => (
            <Card key={category}>
              <CardHeader className="bg-emerald-50 border-b py-3">
                <CardTitle className="text-lg text-emerald-800 flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  {category} ({items.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {items.map((material) => (
                    <div key={material.material_id} className="p-4 hover:bg-muted/50 transition-colors">
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-primary">{material.name}</h4>
                            {!material.active && (
                              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Inactive</span>
                            )}
                          </div>
                          <p className="text-sm text-secondary mt-1">
                            ₹{(material.price || material.price_per_unit || 0).toFixed(2)} per {material.unit || 'unit'}
                          </p>
                          {material.description && (
                            <p className="text-xs text-muted-foreground mt-1 italic">
                              {material.description}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <Building2 className="h-3 w-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">
                              {material.assigned_franchises?.length || 0} franchise(s) assigned
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(material)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(material.material_id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-primary">Edit Raw Material</DialogTitle>
          </DialogHeader>
          {formContent}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RawMaterialsManagement;
